import React from 'react';

import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { VIEW_OMNIBAR_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import { OmniBar } from '../Omnibar.tsx';

/**
 * Inner component that provides context data to OmniBar.
 */
const OmniBarWrapperInner = () => {
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const spaceId = useSpaceId();
	const { subType } = getCachedPageData(contentId);

	if (!contentId || !spaceId || !contentType) {
		return null;
	}

	return (
		<>
			<OmniBar
				contentId={contentId}
				spaceId={spaceId}
				contentType={contentType}
				contentSubType={subType}
			/>
			<ExperienceStart name={VIEW_OMNIBAR_EXPERIENCE} attributes={{ contentId, contentType }} />
		</>
	);
};

/**
 * Wrapper component that provides context data to OmniBar with error boundary isolation.
 * Errors in the omnibar will be caught and reported without affecting the rest of the page.
 */
export const OmniBarWrapper = (): React.JSX.Element => (
	<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ENGAGEMENT}>
		<OmniBarWrapperInner />
	</TransparentErrorBoundary>
);
